import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from '../../components/buttons/button';
import FormDataViewSkeleton from '../../components/skeletons/FormDataViewSkeleton';
import EmptyState from '../../components/data-display/EmptyState';
import { formatDateWithTimezone, formatTimeWithTimezone } from '../../utils/dateUtils';
import PageLayout from '../../components/layout/PageLayout';
import { useFormDataView } from '../../hooks/useFormDataView';
import { useFileCarousel } from '../../hooks/useFileCarousel';
import { openConfirmationModal } from '../../reducers/uiSlice';
import { confirmationService } from '../../services/confirmationService';
import { toastService } from '../../services/toastService';

const ResponseIcon = () => (
  <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const FileCarousel = ({ files, currentIndex, onClose, onNext, onPrev, onSetIndex }) => {
  const isImage = (file) => (typeof file === 'string' ? (file.match(/\.(jpg|jpeg|png|gif|webp)$/i) || file.startsWith('data:image')) : file.type && file.type.indexOf('image/') === 0);
  const getFileUrl = (file) =>
    typeof file === 'string'
      ? file
      : file.data
      ? URL.createObjectURL(new Blob([file.data], { type: file.type }))
      : URL.createObjectURL(file);
  const getFileName = (file, i) =>
    typeof file === 'string'
      ? (file.startsWith('data:image') ? 'Signature' : file.split('/').pop()) || `File ${i + 1}`
      : file.name || `File ${i + 1}`;

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onPrev, onNext, onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex justify-center items-center">
      <div className="relative w-full h-full flex justify-center items-center">
        <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2 bg-white/10 rounded-full text-white hover:bg-white/20 active:scale-95 transition-all">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {files.length > 1 && (
          <>
            <button onClick={onPrev} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 rounded-full text-white hover:bg-white/20 active:scale-95 transition-all">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button onClick={onNext} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 rounded-full text-white hover:bg-white/20 active:scale-95 transition-all">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        <div className="w-full h-full flex flex-col justify-center items-center px-4">
          {isImage(files[currentIndex]) ? (
            <img src={getFileUrl(files[currentIndex])} alt={getFileName(files[currentIndex], currentIndex)} className="w-full h-auto max-h-[85vh] object-contain" />
          ) : (
            <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-600 rounded-lg flex justify-center items-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-3">{getFileName(files[currentIndex], currentIndex)}</h3>
              <a href={getFileUrl(files[currentIndex])} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 active:scale-95 transition-all">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open File
              </a>
            </div>
          )}
        </div>

        {files.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
            <div className="flex gap-2 bg-black/50 p-2 rounded-lg max-w-full overflow-x-auto">
              {files.map((file, index) => (
                <button key={index} onClick={() => onSetIndex(index)} className={`relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 active:scale-95 transition-all ${index === currentIndex ? 'ring-2 ring-white' : 'opacity-60 hover:opacity-90'}`}>
                  {isImage(file) ? (
                    <img src={getFileUrl(file)} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-800 flex justify-center items-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FormDataView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { form, submission, loading, isDeleting, error, handleDelete, isOfflineSubmission } = useFormDataView(id);
  const [visiblePasswords, setVisiblePasswords] = React.useState(new Set());
  const { 
    showCarousel, 
    carouselFiles, 
    currentIndex,
    setCurrentIndex,
    openCarousel, 
    closeCarousel,
    nextFile,
    prevFile
  } = useFileCarousel();

  const togglePasswordVisibility = (fieldId, forceValue) => {
    setVisiblePasswords(prev => {
      const next = new Set(prev);
      if (forceValue === true) next.add(fieldId);
      else if (forceValue === false) next.delete(fieldId);
      else if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  };

  const handleUrlClick = (url) => {
    const confirmId = `redirect-${Math.random().toString(36).substr(2, 9)}`;
    confirmationService.register(confirmId, () => {
      window.open(url.startsWith('http') ? url : `https://${url}`, '_blank', 'noopener,noreferrer');
    });
    
    dispatch(openConfirmationModal({
      title: 'External Redirect',
      message: `You are about to be redirected to an external website: ${url}. Do you want to proceed?`,
      type: 'warning',
      confirmId,
    }));
  };

  const truncateAddress = (address, maxLength = 50) => {
    if (!address || address === 'N/A') return 'N/A';
    if (address.length <= maxLength) return address;
    return address.substring(0, maxLength) + '...';
  };

  const formatDisplayValue = (value, options = []) => {
    const getLabel = (val) => {
      if (!options || !Array.isArray(options)) return val;
      const option = options.find(opt => String(opt.value) === String(val));
      return option ? option.label : val;
    };

    if (Array.isArray(value)) {
      return value.map(item => {
        if (typeof item === 'object' && item !== null) {
          return item.label || item.value || item.name || String(item);
        }
        return getLabel(item);
      }).join(', ');
    }
    if (typeof value === 'string' && value.trim() !== '') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map(item => {
            if (typeof item === 'object' && item !== null) {
              return item.label || item.value || item.name || String(item);
            }
            return getLabel(item);
          }).join(', ');
        }
        return getLabel(value);
      } catch {
        return getLabel(value);
      }
    }
    return getLabel(value);
  };

  const renderFieldValue = (value, field) => {
    if (value === null || value === undefined || value === '') return <span className="text-gray-500">Not provided</span>;
    
    const type = field?.type?.toUpperCase();
    const fieldId = field?.id;

    if (type === 'PASSWORD') {
      const isVisible = visiblePasswords.has(fieldId);
      return (
        <div className="flex items-center gap-2">
          <span className="font-mono bg-gray-50 px-2 py-1 rounded border border-gray-100 min-w-[140px] inline-flex justify-center">
            {isVisible ? value : '••••••••••••'}
          </span>
          <button 
            type="button"
            onMouseDown={() => togglePasswordVisibility(fieldId, true)}
            onMouseUp={() => togglePasswordVisibility(fieldId, false)}
            onMouseLeave={() => togglePasswordVisibility(fieldId, false)}
            onTouchStart={() => togglePasswordVisibility(fieldId, true)}
            onTouchEnd={() => togglePasswordVisibility(fieldId, false)}
            className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded-lg hover:bg-blue-50 active:scale-90"
            title="Hold to show password"
          >
            {isVisible ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
      );
    }

    if (type === 'URL') {
      return (
        <button 
          type="button"
          onClick={() => handleUrlClick(value)}
          className="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-600 transition-all font-medium break-all text-left"
        >
          {value}
        </button>
      );
    }
    if (type === 'AUDIO') {
      const rawValues = Array.isArray(value) ? value : [value];
      const fileUrls = rawValues.filter(item => 
        (typeof item === 'string' && item.trim() !== '') || 
        (typeof item === 'object' && item !== null && (item.data || item.name))
      );
      
      if (fileUrls.length > 0) {
        return (
          <div className="flex flex-col gap-2 w-full max-w-md">
            {fileUrls.map((item, idx) => {
              const fileUrl = typeof item === 'string' 
                ? item 
                : (item.data ? URL.createObjectURL(new Blob([item.data], { type: item.type })) : null);
              
              if (!fileUrl) return null;
              return <audio key={idx} controls src={fileUrl} className="w-full h-11 rounded" />;
            })}
          </div>
        );
      }
    }

    if (type && (['IMAGE', 'FILE', 'MULTI-FILE', 'FILE_MULTIPLE', 'DOCUMENT', 'VIDEO', 'SIGNATURE'].includes(type))) {
      const rawValues = Array.isArray(value) ? value : [value];

      if (rawValues.length > 0) {
        const fileUrls = rawValues.filter(item => 
          (typeof item === 'string' && item.trim() !== '') || 
          (typeof item === 'object' && item !== null && (item.data || item.name))
        );
        
        if (fileUrls.length > 0) {
          return (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {fileUrls.map((item, idx) => {
                  const isString = typeof item === 'string';
                  
                  const isImg = isString 
                    ? (item.startsWith('data:image') || item.match(/\.(jpg|jpeg|png|gif|webp)$/i))
                    : item.type?.startsWith('image/');
                  
                  const fileUrl = isString 
                    ? item 
                    : (item.data ? URL.createObjectURL(new Blob([item.data], { type: item.type })) : null);
                  
                  const fileName = isString 
                    ? (item.startsWith('data:image') ? 'Signature' : item.split('/').pop())
                    : (item.name || `File ${idx + 1}`);

                  return (
                    <div 
                      key={idx} 
                      className="relative w-20 h-20 bg-gray-100 rounded-lg overflow-hidden cursor-pointer group active:scale-95 transition-all border border-gray-200" 
                      onClick={() => openCarousel(fileUrls, idx)}
                    >
                      {isImg && fileUrl ? (
                        <img src={fileUrl} alt={fileName} className="w-full h-full object-contain bg-white" /> 
                      ) : (
                        <div className="w-full h-full flex justify-center items-center bg-gray-100">
                          <div className="text-center">
                            <div className="w-8 h-8 mx-auto bg-blue-600 rounded flex justify-center items-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      )}
                      {!isString && item.syncStatus === 'pending' && (
                        <div className="absolute top-1 right-1 w-2 h-2 bg-yellow-500 rounded-full" title="Pending sync" />
                      )}
                    </div>
                  );
                })}
              </div>
              <p 
                className="text-xs text-gray-500 font-medium cursor-pointer hover:text-blue-600 transition-colors"
                onClick={() => openCarousel(fileUrls, 0)}
              >
                {fileUrls.length} {fileUrls.length === 1 ? 'file' : 'files'} • Click to view
              </p>
            </div>
          );
        }
      }
      return formatDisplayValue(value, field.options);
    }
    return formatDisplayValue(value, field.options);
  };

  if (loading) {
    return (
      <PageLayout title="Submission Detail" showStatus>
        <FormDataViewSkeleton />
      </PageLayout>
    );
  }
  
  const getErrorTitle = (msg) => {
    if (msg?.toLowerCase().includes('permission') || msg?.toLowerCase().includes('authorized')) {
      return "Access Denied";
    }
    return "Cannot View Submission";
  };

  return (
    <>
      <PageLayout title="Submission Detail" showStatus>
        {error ? (
          <div className="flex justify-center items-center min-h-[60vh]">
            <EmptyState title={getErrorTitle(error)} message={error} />
          </div>
        ) : !submission || !form ? (
          <div className="flex justify-center items-center min-h-[60vh]">
            <EmptyState title="Submission Not Found" message="This submission may have been deleted or the form is not available offline." />
          </div>
        ) : (
          <div className="space-y-4 pb-8">
            <div>
              <h1 className="font-bold text-gray-900 text-lg mb-1">{form.title}</h1>
              <p className="text-gray-600 text-sm">{form.description}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 border-2 border-gray-200 p-3 rounded-lg">
                <p className="text-sm text-gray-500 font-medium mb-1">Location</p>
                <p className="text-sm font-semibold text-gray-700" title={submission.address || 'N/A'}>
                  {truncateAddress(submission.address, 65)}
                </p>
              </div>
              <div className="bg-gray-50 border-2 border-gray-200 p-3 rounded-lg">
                <p className="text-sm text-gray-500 font-medium mb-1">Date & Time</p>
                <p className="text-sm font-semibold text-gray-700">{formatDateWithTimezone(submission.created_at)}</p>
                <p className="text-xs text-gray-500 font-medium">{formatTimeWithTimezone(submission.created_at)}</p>
              </div>
            </div>
            
            <div className="space-y-6 pt-2">
              {form.sections.map((section) => {
                const isRoot = section.name === '__root__';
                const sectionDisplayName = section.label || (isRoot ? 'General Section' : (section.name || 'Form Section'));
                const displayFields = section.fields.filter(f => !['start', 'end'].includes(f.type?.toLowerCase()));
                
                if (displayFields.length === 0) return null;

                return (
                  <div key={section.id}>
                    <h2 className="text-lg font-bold text-gray-800 pb-2 mb-4 border-b-2 border-gray-200">{sectionDisplayName}</h2>
                  <div className="space-y-4">
                    {displayFields.map((item, index) => (
                      <div key={item.id}>
                        <p className="text-md font-semibold text-gray-700 mb-2">{index + 1}. {item.label || item.name}</p>
                        <div className="flex justify-start items-start gap-2">
                          <ResponseIcon />
                          <div className="text-sm font-medium text-gray-600 flex-1">
                            {renderFieldValue(item.answer, item)}
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="pt-4 flex gap-3">
              <Button 
                onClick={() => navigate(`/forms/edit/${submission.form_id}/${submission.id}`)} 
                variant="primary" 
                fullWidth 
                size="large" 
                className="font-semibold"
              >
                <svg className="w-5 h-5 text-white mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Submission
              </Button>
            </div>
          </div>
        )}
      </PageLayout>
      
      {showCarousel && (
        <FileCarousel 
          files={carouselFiles} 
          currentIndex={currentIndex}
          onClose={closeCarousel} 
          onNext={nextFile} 
          onPrev={prevFile}
          onSetIndex={setCurrentIndex}
        />
      )}
    </>
  );
};

export default FormDataView;